import { useState } from 'react';
import { FormatPreview } from './components/FormatPreview.jsx';
import { copyText } from './lib/util.js';

const START_ABOUT = '^2Open now! ^*^=Strawberry Gas^r ^2is selling ^9^_Strawberry Water^r';

const CARET_CODES = [
  ['^1–^9', 'color'],
  ['^*', 'bold'],
  ['^_', 'underline'],
  ['^~', 'strike'],
  ['^=', 'underline + strike'],
  ['^r', 'reset'],
  ['^^', 'literal ^'],
];

export default function App() {
  const [content, setContent] = useState(START_ABOUT);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const ok = await copyText(content);
    setCopied(ok);
    if (ok) setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="min-h-screen bg-void bg-grid text-snow">
      <header className="sticky top-0 z-40 border-b border-edge bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
          <span className="flex shrink-0 items-center gap-3">
            <span className="grid h-9 w-9 place-items-center border border-neon/60 bg-void font-mono text-neon">
              ^
            </span>
            <span className="font-mono text-sm font-bold uppercase tracking-[0.3em] text-snow">
              Skyad<span className="text-neon">Editor</span>
            </span>
          </span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-dim">
            type · preview · copy
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Editor */}
          <section className="flex flex-col border border-edge bg-panel">
            <div className="flex items-center justify-between border-b border-edge px-4 py-2.5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
                editor · raw ^codes
              </p>
              <button
                type="button"
                onClick={handleCopy}
                disabled={!content.trim()}
                className="inline-flex select-none items-center justify-center gap-2 rounded-none px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-widest bg-neon text-black shadow-neon-teal transition-colors hover:bg-[#6bffe9] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {copied ? 'Copied!' : 'Copy Snippet'}
              </button>
            </div>

            <div className="p-4">
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={12}
                spellCheck={false}
                autoComplete="off"
                placeholder="Type your /ad snippet using ^ formatting…"
                className="min-h-[260px] w-full resize-y border border-edge bg-void px-3 py-3 font-mono text-sm leading-relaxed text-mist outline-none placeholder:text-dim focus:border-neon focus:shadow-neon-teal"
              />
            </div>
          </section>

          {/* Preview window */}
          <section className="flex flex-col border border-edge bg-void bg-grid">
            <div className="flex items-center justify-between border-b border-edge px-4 py-2.5">
              <p className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
                live in-game preview
              </p>
              <span className="h-2 w-2 rounded-full bg-neon animate-pulse" />
            </div>

            <div className="flex flex-1 items-center justify-center p-8">
              {content.trim() ? (
                <FormatPreview
                  content={content}
                  className="font-mono text-base leading-relaxed"
                />
              ) : (
                <p className="font-mono text-[11px] uppercase tracking-widest text-dim">
                  Start typing to preview
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Caret code cheat sheet */}
        <div className="mt-8 border border-edge bg-surface/40 p-4">
          <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-dim">
            Caret codes
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-mist">
            {CARET_CODES.map(([code, effect]) => (
              <span key={code}>
                <span className="text-neon">{code}</span>
                <span className="text-dim"> — {effect}</span>
              </span>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-edge py-6 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">
          Skyad Editor · caret-formatted /ad snippets · Made by Prince.
        </p>
      </footer>
    </div>
  );
}